import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Save, ArrowUp, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { studioHeaders } from "@/lib/studioAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);

export default function TravelZonesEditor() {
    const [zones, setZones] = useState([]);
    const [orig, setOrig] = useState("");
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const { data } = await axios.get(`${API}/travel-zones`, { params: { _t: Date.now() } });
            setZones(data.zones || []);
            setOrig(JSON.stringify(data.zones || []));
        } catch { toast.error("Failed to load travel zones"); }
    };
    useEffect(() => { load(); }, []);

    const up = (i, k, v) => setZones((p) => p.map((z, idx) => (idx === i ? { ...z, [k]: v } : z)));
    const move = (i, d) => setZones((p) => { const n = [...p]; const j = i + d; if (j < 0 || j >= n.length) return p; [n[i], n[j]] = [n[j], n[i]]; return n; });
    const add = () => setZones((p) => [...p, { id: `zone-${p.length + 1}`, label_en: "New zone", label_cn: "", mode: "fixed", fee_rm: 0, kv: false }]);
    const dirty = JSON.stringify(zones) !== orig;

    const save = async () => {
        setSaving(true);
        try {
            const body = { zones: zones.map((z) => ({ ...z, id: slug(z.id || z.label_en), fee_rm: z.mode === "fixed" ? parseInt(z.fee_rm || 0, 10) : null })) };
            await axios.put(`${API}/admin/travel-zones`, body, { headers: studioHeaders() });
            toast.success("Travel zones saved · live on pricing page + checkout");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    return (
        <div className="bg-cafe-latte/60 border border-border rounded-3xl p-6 md:p-8 mb-8" data-testid="travel-zones-editor">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <div>
                    <h3 className="font-display text-xl text-cafe-espresso">Travel zones & fees</h3>
                    <p className="text-xs text-cafe-mocha mt-1">Drives the Travel & Location Policy table and the checkout travel charge. "Klang Valley" zones are included free on Brand Campaign packages.</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={add} data-testid="travel-zone-add" className="rounded-full border-cafe-espresso/20 bg-transparent hover:bg-cafe-cream h-9 px-4 text-xs"><Plus className="h-3.5 w-3.5 mr-1" /> Add zone</Button>
                    <Button onClick={save} disabled={!dirty || saving} data-testid="travel-zones-save" className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-9 px-4 text-xs"><Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving…" : dirty ? "Save zones" : "Saved"}</Button>
                </div>
            </div>
            <div className="space-y-2">
                {zones.map((z, i) => (
                    <div key={i} className="grid grid-cols-2 md:grid-cols-[1.4fr_1.2fr_1fr_0.7fr_0.8fr_auto] gap-2 items-end bg-cafe-cream/70 border border-border rounded-2xl p-3" data-testid={`travel-zone-edit-${z.id}`}>
                        <div><Label className="text-[10px] text-cafe-mocha">Label (EN)</Label><Input value={z.label_en} onChange={(e) => up(i, "label_en", e.target.value)} data-testid={`travel-zone-label-${z.id}`} className="mt-1 h-9 bg-white border-border rounded-lg text-sm" /></div>
                        <div><Label className="text-[10px] text-cafe-mocha">标签 (CN)</Label><Input value={z.label_cn || ""} onChange={(e) => up(i, "label_cn", e.target.value)} className="mt-1 h-9 bg-white border-border rounded-lg text-sm" /></div>
                        <div>
                            <Label className="text-[10px] text-cafe-mocha">Charge type</Label>
                            <select value={z.mode} onChange={(e) => up(i, "mode", e.target.value)} data-testid={`travel-zone-mode-${z.id}`} className="mt-1 w-full h-9 bg-white border border-border rounded-lg px-2 text-sm">
                                <option value="fixed">Fixed fee</option>
                                <option value="quote">Custom quotation</option>
                                <option value="tbc">To be confirmed</option>
                            </select>
                        </div>
                        <div><Label className="text-[10px] text-cafe-mocha">Fee (RM)</Label><Input type="number" min="0" disabled={z.mode !== "fixed"} value={z.mode === "fixed" ? (z.fee_rm ?? 0) : ""} onChange={(e) => up(i, "fee_rm", e.target.value)} data-testid={`travel-zone-fee-input-${z.id}`} className="mt-1 h-9 bg-white border-border rounded-lg text-sm font-mono" /></div>
                        <div className="flex items-center gap-2 pb-2"><Switch checked={!!z.kv} onCheckedChange={(v) => up(i, "kv", v)} data-testid={`travel-zone-kv-${z.id}`} /><Label className="text-[10px] text-cafe-mocha">Klang Valley</Label></div>
                        <div className="flex gap-1 pb-0.5">
                            <Button variant="outline" size="icon" disabled={i === 0} onClick={() => move(i, -1)} className="h-8 w-8 rounded-full border-cafe-espresso/20 bg-transparent"><ArrowUp className="h-3.5 w-3.5" /></Button>
                            <Button variant="outline" size="icon" disabled={i === zones.length - 1} onClick={() => move(i, 1)} className="h-8 w-8 rounded-full border-cafe-espresso/20 bg-transparent"><ArrowDown className="h-3.5 w-3.5" /></Button>
                            <Button variant="outline" size="icon" onClick={() => setZones((p) => p.filter((_, idx) => idx !== i))} data-testid={`travel-zone-delete-${z.id}`} className="h-8 w-8 rounded-full border-cafe-espresso/20 bg-transparent hover:bg-red-50 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
